import React, { useEffect, useState } from "react"
import CarrotClient from "carrot-api"
import { useBodyClass } from "utils/Hooks"
import { RegisterError } from "components/views/fle/register/RegisterError"
import Settings from "utils/CarrotConfig"
import NiceToSeeYou from "components/views/fle/register/NiceToSeeYou"
import FinancialSupport from "components/views/fle/register/FinancialSupport"
import Answers from "components/views/fle/register/Answers"
import Access from "components/views/fle/register/Access"
import SetPassword from "components/views/fle/register/SetPassword"
import AcknowledgeTerms from "components/views/fle/register/AcknowledgeTerms"
import HealthConsent from "components/views/fle/register/HealthConsent"
import EmailOptIn from "./EmailOptIn"
import PersonalEmail from "./PersonalEmail"
import { currentlySetRegistrationCohort } from "../../../context/user/UserContext"
import GuidanceInformational from "./Guidance"
import { PreferredName } from "./PreferredName"
import PersonalEmailVerification from "./PersonalEmailVerification"
import { getHeap } from "utils/heap"
import RegisteringLoadingIndicator from "./RegistrationLoadingIndicator"
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk"
import { reportError } from "../../../../utils/ErrorReporting"
import { useIntlMessages } from "components/views/app/IntlMessages"
import { LoadingInner } from "components/views/Loading"
import { usePreferredLocaleContext } from "components/context/user/PreferredLocaleContext"
import { WhatKindOfJourney } from "./WhatKindOfJourney"
import { useJourneyOptions } from "../../../carrot-plans/hooks/useJourneyOptions"
import { ProgramType } from "../../../dynamic-content/Benefit"
import { useJourneyExperiments, useTrackExperimentEvent } from "./hooks/useExperiments"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

enum STEPS {
  LOADING,
  INTRO,
  FINANCIAL_SUPPORT,
  ANSWERS,
  ACCESS,
  GUIDANCE_INFORMATIONAL,
  SET_PASSWORD,
  PERSONAL_EMAIL,
  PREFERRED_NAME,
  ACKNOWLEDGE_TERMS,
  HEALTH_CONSENT,
  EMAIL_OPT_IN,
  ERROR,
  PERSONAL_EMAIL_VERIFICATION,
  WHAT_KIND_OF_JOURNEY
}

interface RegistrationJourney {
  journeyType: string
  somethingElseJourney: string
  skipped: boolean
}

const RegisterWizard = (props) => {
  const [step, setStep] = useState(STEPS.LOADING)
  const [firstName, setFirstName] = useState(null)
  const [guid, setGuid] = useState(null)
  const [email, setEmail] = useState(null)
  const [emailOptIn, setEmailOptIn] = useState(false)
  const [companyId, setCompanyId] = useState(0)
  const [parentCompanyId, setParentCompanyId] = useState(0)
  const [companyName, setCompanyName] = useState(null)
  const [currencySymbol, setCurrencySymbol] = useState(null)
  const [isCarrotCore, setIsCarrotCore] = useState(null)
  const [password, setPassword] = useState(null)
  const [readPolicy, setReadPolicy] = useState(false)
  const [readTerms, setReadTerms] = useState(false)
  const [healthConsent, setHealthConsent] = useState(false)
  const [requiresSaml, setRequiresSaml] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [benefitConfiguration, setBenefitConfiguration] = useState(null)
  const [benefit, setBenefit] = useState(null)
  const [personalEmail, setPersonalEmail] = useState(null)
  const [preferredName, setPreferredName] = useState(null)
  const [isUsa, setIsUsa] = useState(null)
  const [companyPlans, setCompanyPlans] = useState([])
  const [supportsOOPM, setSupportsOOPM] = useState(null)
  const [journey, setJourney] = useState(null)
  const [somethingElseJourney, setSomethingElseJourney] = useState(null)
  const [skipped, setSkipped] = useState(null)
  const ldClient = useLDClient()
  const { didCompiledMessagesLoad } = useIntlMessages()
  const [showPage, setShowPage] = useState(false)
  const { preferredLocale } = usePreferredLocaleContext()
  const { enableMemberJourneyModularity, enablePostpartumRoutingSupport } = useFlags()
  const { afterWelcomeScreen, afterPrefName, control } = useJourneyExperiments()
  const { options: journeyOptions } = useJourneyOptions(
    benefit,
    enableMemberJourneyModularity,
    enablePostpartumRoutingSupport
  )
  const { trackExperimentEvent } = useTrackExperimentEvent()

  let registrationJourney: RegistrationJourney = {
    journeyType: journey,
    somethingElseJourney: somethingElseJourney,
    skipped: skipped
  }

  useBodyClass("bg-warm-gray")

  useEffect(() => {
    if (didCompiledMessagesLoad) {
      setShowPage(true)
    }
  }, [didCompiledMessagesLoad])

  useEffect(() => {
    async function fetchRegisteringUser(): Promise<void> {
      await client.logout()
      const guidFromParams = props.match.params.guid
      setGuid(guidFromParams)
      const registeringUser = await client.getRegisteringUser(guidFromParams)
      if (!registeringUser) {
        setStep(STEPS.ERROR)
        return
      }
      setFirstName(registeringUser.firstName)
      setEmail(registeringUser.email)
      setCompanyName(registeringUser.companyName)
      setCompanyId(registeringUser.companyId)
      setParentCompanyId(registeringUser.parentCompanyId)
      setCurrencySymbol(registeringUser.currencySymbol)
      setIsCarrotCore(registeringUser.benefit.program.type === ProgramType.CORE)
      setRequiresSaml(registeringUser.requiresSaml)
      setBenefitConfiguration(registeringUser.benefitConfiguration)
      setBenefit(registeringUser.benefit)
      setIsUsa(registeringUser.isUsa)
      setPreferredName(registeringUser.preferredName)
      setEmailOptIn(!!registeringUser.isUsa)
      setStep(STEPS.INTRO)
      setCompanyPlans(registeringUser.companyPlanList)
      setSupportsOOPM(registeringUser.hasOutOfPocketMaxSupport)
    }

    fetchRegisteringUser().catch((error) => reportError(error))
  }, [props.match.params.guid])

  useEffect(() => {
    async function loadLDFlagsForRegisteringUser(memberEmail, memberCompanyId, memberParentCompanyId): Promise<void> {
      if (ldClient && memberEmail && memberCompanyId) {
        await ldClient.waitUntilReady()

        const ldUser = {
          key: memberEmail,
          email: memberEmail,
          custom: { companyId: memberCompanyId }
        }

        if (memberParentCompanyId !== 0) {
          ldUser.custom["parentCompanyId"] = memberParentCompanyId
        }

        await ldClient.identify(ldUser)
      }
    }

    loadLDFlagsForRegisteringUser(email, companyId, parentCompanyId).catch((error) => reportError(error))
  }, [ldClient, email, companyId, parentCompanyId])

  async function createUser(): Promise<void> {
    if (!isSubmitting) {
      try {
        if (control) {
          registrationJourney = null
        }
        setIsSubmitting(true)
        const registrationCohort = currentlySetRegistrationCohort
        await client.createAccount(
          guid,
          password,
          emailOptIn,
          readTerms,
          readPolicy,
          healthConsent,
          personalEmail,
          registrationCohort,
          preferredName,
          registrationJourney,
          preferredLocale
        )
      } catch {
        window.location.href = "/"
      }
      await trackExperimentEvent("registration-complete")
      getHeap().track("MemberRegistrationCompleted", {
        EventName: "MemberRegistrationCompleted"
      })
      if (!personalEmail) {
        redirectToRegistrationComplete()
      } else {
        await sendVerificationEmailAndSetStep()
      }
    }
  }

  async function sendVerificationEmailAndSetStep(): Promise<void> {
    try {
      setIsSubmitting(true)
      await client.sendPersonalEmailVerificationEmail()
    } catch {
      window.location.href = "/"
    }
    setStep(STEPS.PERSONAL_EMAIL_VERIFICATION)
  }

  function onCompleteSetPassword(password?: string): void {
    setPassword(password)
    setStep(STEPS.PERSONAL_EMAIL)
  }

  function onSkipNullOutPersonalEmail(): void {
    setPersonalEmail(null)
    setStepAfterEmail()
  }

  async function onCompleteIntro(): Promise<void> {
    trackExperimentEvent("registration-welcome-continue")
    if (afterWelcomeScreen) {
      setStep(STEPS.WHAT_KIND_OF_JOURNEY)
    } else {
      setStep(isCarrotCore ? STEPS.ANSWERS : STEPS.FINANCIAL_SUPPORT)
    }
  }

  async function onCompleteJourney(): Promise<void> {
    trackExperimentEvent("registration-journey-select-screen-to-next")
    if (afterWelcomeScreen) {
      setStep(isCarrotCore ? STEPS.ANSWERS : STEPS.FINANCIAL_SUPPORT)
    } else {
      setStep(STEPS.ACKNOWLEDGE_TERMS)
    }
  }

  function setStepAfterEmail(): void {
    preferredName
      ? afterPrefName
        ? setStep(STEPS.WHAT_KIND_OF_JOURNEY)
        : setStep(STEPS.ACKNOWLEDGE_TERMS)
      : setStep(STEPS.PREFERRED_NAME)
  }

  if (!showPage) {
    return <LoadingInner />
  }
  switch (step) {
    case STEPS.INTRO:
      return <NiceToSeeYou companyName={companyName} onComplete={onCompleteIntro} />
    case STEPS.FINANCIAL_SUPPORT:
      return (
        <FinancialSupport
          companyName={companyName}
          companyPlans={companyPlans}
          currencySymbol={currencySymbol}
          supportsOOPM={supportsOOPM}
          onBack={() => (afterWelcomeScreen ? setStep(STEPS.WHAT_KIND_OF_JOURNEY) : setStep(STEPS.INTRO))}
          onComplete={() => setStep(STEPS.ANSWERS)}
        />
      )
    case STEPS.ANSWERS:
      return (
        <Answers
          onBack={() => setStep(isCarrotCore ? STEPS.INTRO : STEPS.FINANCIAL_SUPPORT)}
          onComplete={() => setStep(STEPS.ACCESS)}
        />
      )
    case STEPS.ACCESS:
      return (
        <Access
          journeyModularityEnabled={enableMemberJourneyModularity}
          isCarrotCore={isCarrotCore}
          journeyOptions={journeyOptions}
          onBack={() => setStep(STEPS.ANSWERS)}
          onComplete={() => setStep(STEPS.GUIDANCE_INFORMATIONAL)}
        />
      )
    case STEPS.GUIDANCE_INFORMATIONAL:
      return (
        <GuidanceInformational
          isCarrotCore={isCarrotCore}
          onBack={() => setStep(STEPS.ACCESS)}
          onComplete={() => setStep(requiresSaml ? STEPS.PERSONAL_EMAIL : STEPS.SET_PASSWORD)}
        />
      )
    case STEPS.SET_PASSWORD:
      return (
        <SetPassword
          email={email}
          onBack={() => setStep(STEPS.GUIDANCE_INFORMATIONAL)}
          onComplete={onCompleteSetPassword}
        />
      )
    case STEPS.PERSONAL_EMAIL:
      return (
        <PersonalEmail
          personalEmail={personalEmail || ""}
          email={email}
          setPersonalEmail={setPersonalEmail}
          onBack={() => setStep(requiresSaml ? STEPS.ACCESS : STEPS.SET_PASSWORD)}
          onComplete={setStepAfterEmail}
          onSkip={onSkipNullOutPersonalEmail}
        />
      )
    case STEPS.PREFERRED_NAME:
      return (
        <PreferredName
          {...{ setPreferredName, preferredName }}
          onBack={() => setStep(STEPS.PERSONAL_EMAIL)}
          onComplete={() => (afterPrefName ? setStep(STEPS.WHAT_KIND_OF_JOURNEY) : setStep(STEPS.ACKNOWLEDGE_TERMS))}
        />
      )
    case STEPS.ACKNOWLEDGE_TERMS:
      return (
        <AcknowledgeTerms
          {...{ firstName, preferredName, readPolicy, setReadPolicy, readTerms, setReadTerms }}
          onBack={() =>
            afterPrefName
              ? setStep(STEPS.WHAT_KIND_OF_JOURNEY)
              : preferredName
              ? setStep(STEPS.PERSONAL_EMAIL)
              : setStep(STEPS.PREFERRED_NAME)
          }
          onComplete={() => setStep(STEPS.HEALTH_CONSENT)}
        />
      )
    case STEPS.HEALTH_CONSENT:
      return (
        <HealthConsent
          isSubmitting={isSubmitting}
          healthConsent={healthConsent}
          setHealthConsent={setHealthConsent}
          benefitConfiguration={benefitConfiguration}
          onBack={() => setStep(STEPS.ACKNOWLEDGE_TERMS)}
          onComplete={isUsa ? createUser : () => setStep(STEPS.EMAIL_OPT_IN)}
        />
      )
    case STEPS.EMAIL_OPT_IN:
      return (
        <EmailOptIn
          isSubmitting={isSubmitting}
          emailOptIn={emailOptIn}
          setEmailOptIn={setEmailOptIn}
          onBack={() => setStep(STEPS.HEALTH_CONSENT)}
          onComplete={createUser}
        />
      )
    case STEPS.PERSONAL_EMAIL_VERIFICATION:
      return <PersonalEmailVerification personalEmail={personalEmail} onComplete={redirectToRegistrationComplete} />
    case STEPS.WHAT_KIND_OF_JOURNEY:
      return (
        <WhatKindOfJourney
          journey={journey}
          setJourney={setJourney}
          somethingElseJourney={somethingElseJourney}
          setSomethingElseJourney={setSomethingElseJourney}
          skipped={skipped}
          setSkipped={setSkipped}
          journeyOptions={journeyOptions}
          isCarrotCore={isCarrotCore}
          onBack={() =>
            afterWelcomeScreen
              ? setStep(STEPS.INTRO)
              : preferredName
              ? setStep(STEPS.PERSONAL_EMAIL)
              : setStep(STEPS.PREFERRED_NAME)
          }
          onComplete={onCompleteJourney}
        />
      )
    case STEPS.ERROR:
      return <RegisterError />
    default:
      return <RegisteringLoadingIndicator />
  }
}

export function redirectToRegistrationComplete() {
  window.location.href = "/registration/complete"
  /*
   *  The usage of window.location.href over 'history' is intentional.
   *  This is due to how we determine if a user is logged or not, and
   *  we want to trigger a server refresh to force our app to see that
   *  we're now logged in.
   */
}

export default RegisterWizard
